import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getSriLankaDateString, getMinSelectableDate, getMaxSelectableDate } from '../utils/dateUtils';
import { routeApi, scheduleApi, bookingApi } from '../services/api';
import { Bus, Calendar, Clock, Download, Loader2, AlertCircle, MapPin, CheckCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Swal from 'sweetalert2';
import { COMPANY_CONFIG } from '../config/config';

const SeatStatus = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedDate, setSelectedDate] = useState(getSriLankaDateString());
  const [availableRoutes, setAvailableRoutes] = useState([]);
  const [selectedScheduleDateId, setSelectedScheduleDateId] = useState('');
  const [selectedRouteInfo, setSelectedRouteInfo] = useState(null);
  const [scheduleDateDetails, setScheduleDateDetails] = useState(null);
  const [bookedSeatsData, setBookedSeatsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [error, setError] = useState('');
  const todayDate = getSriLankaDateString();

  // Check if user is logged in and has correct role
  useEffect(() => {
    if (!user || user.role !== 3) {
      navigate('/');
    }
  }, [user, navigate]);

  // Load routes for selected date when date changes
  useEffect(() => {
    if (selectedDate) {
      loadRoutesForDate();
    } else {
      setAvailableRoutes([]);
      setSelectedRouteId('');
      setSelectedScheduleDateId('');
      setSelectedRouteInfo(null);
      setScheduleDateDetails(null);
      setBookedSeatsData([]);
    }
  }, [selectedDate]);

  // Load schedule date and seats when route is selected
  useEffect(() => {
    if (selectedRouteId && selectedDate) {
      loadScheduleDateAndSeats();
    } else {
      setSelectedScheduleDateId('');
      setSelectedRouteInfo(null);
      setScheduleDateDetails(null);
      setBookedSeatsData([]);
    }
  }, [selectedRouteId, selectedDate]);

  // Load routes for selected date (similar to ManagerDashboard)
  const loadRoutesForDate = async () => {
    if (!selectedDate || !user?.username) {
      return;
    }

    try {
      setLoadingRoutes(true);
      setError('');

      // Get all active routes
      const routesResponse = await routeApi.getRoutes(true);
      let allRoutes = [];
      
      if (routesResponse?.value && Array.isArray(routesResponse.value)) {
        allRoutes = routesResponse.value;
      } else if (Array.isArray(routesResponse)) {
        allRoutes = routesResponse;
      }

      // Get all schedules
      const schedulesResponse = await scheduleApi.getSchedules();
      let allSchedules = [];
      
      if (schedulesResponse?.value && Array.isArray(schedulesResponse.value)) {
        allSchedules = schedulesResponse.value;
      } else if (Array.isArray(schedulesResponse)) {
        allSchedules = schedulesResponse;
      }

      // Filter schedules by bus number
      const busNumber = user.username;
      const busSchedules = allSchedules.filter(schedule => {
        const vehicleReg = schedule.vehicle?.registrationNumber || schedule.registrationNumber || '';
        return vehicleReg.endsWith(busNumber) && schedule.isActive !== false;
      });

      // Get schedules for each route and filter by date
      const routesWithSchedules = [];
      
      for (const route of allRoutes) {
        try {
          const routeId = route.routeID || route.id;
          const routeSchedules = busSchedules.filter(s => 
            (s.routeID || s.routeId) === routeId
          );

          // Check if any schedule has a schedule date for the selected date
          for (const schedule of routeSchedules) {
            const scheduleId = schedule.scheduleID || schedule.id;
            
            try {
              const scheduleDatesResponse = await scheduleApi.getScheduleDatesBySchedule(scheduleId, selectedDate);
              let scheduleDates = [];
              
              if (scheduleDatesResponse?.value && Array.isArray(scheduleDatesResponse.value)) {
                scheduleDates = scheduleDatesResponse.value;
              } else if (Array.isArray(scheduleDatesResponse)) {
                scheduleDates = scheduleDatesResponse;
              }

              const matchingScheduleDate = scheduleDates.find(sd => {
                const dateStr = sd.date?.split('T')[0] || sd.date;
                return dateStr === selectedDate;
              });

              if (matchingScheduleDate) {
                // Get route city names
                const fromCity = route.fromCity?.cityName || route.fromCityName || route.fromLocation || 'Unknown';
                const toCity = route.toCity?.cityName || route.toCityName || route.toLocation || 'Unknown';
                
                routesWithSchedules.push({
                  routeId: routeId,
                  scheduleId: scheduleId,
                  scheduleDateId: matchingScheduleDate.scheduleDateID || matchingScheduleDate.scheduleDateId || matchingScheduleDate.id,
                  routeName: `${fromCity} -> ${toCity}`,
                  scheduleName: schedule.scheduleName || 'Standard',
                  departureTime: matchingScheduleDate.departureTime || schedule.departureTime || 'N/A',
                  arrivalTime: matchingScheduleDate.arrivalTime || schedule.arrivalTime || 'N/A',
                  busNumber: schedule.vehicle?.registrationNumber || schedule.vehicle?.busNumber || schedule.busNumber || 'N/A'
                });
                break; // Only add route once
              }
            } catch (scheduleDateError) {
              // Skip schedules with errors fetching dates
              continue;
            }
          }
        } catch (error) {
          // Skip routes with errors
          continue;
        }
      }

      setAvailableRoutes(routesWithSchedules);
    } catch (err) {
      console.error('Error loading routes:', err);
      setError('Failed to load routes');
      setAvailableRoutes([]);
    } finally {
      setLoadingRoutes(false);
    }
  };

  // Load schedule date and seats when route is selected
  const loadScheduleDateAndSeats = async () => {
    if (!selectedRouteId || !selectedDate) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Find the selected route info - selectedRouteId is in format "routeId_scheduleId"
      const routeInfo = availableRoutes.find(r => 
        `${r.routeId}_${r.scheduleId}` === selectedRouteId || r.routeId == selectedRouteId
      );
      if (!routeInfo) {
        setError('Route information not found');
        return;
      }

      setSelectedRouteInfo(routeInfo);
      setSelectedScheduleDateId(routeInfo.scheduleDateId);

      // Get schedule date details
      const scheduleDateResponse = await scheduleApi.getScheduleDate(routeInfo.scheduleDateId);
      let scheduleDateData = scheduleDateResponse?.value || scheduleDateResponse?.data || scheduleDateResponse;
      setScheduleDateDetails(scheduleDateData);

      // Get bookings for this schedule date
      const bookingsResponse = await bookingApi.getAllBookings({
        fromDate: selectedDate,
        toDate: selectedDate
      });

      let bookings = [];
      if (bookingsResponse?.value && Array.isArray(bookingsResponse.value)) {
        bookings = bookingsResponse.value;
      } else if (Array.isArray(bookingsResponse)) {
        bookings = bookingsResponse;
      }

      // Filter bookings by schedule date ID and non-cancelled status
      const activeBookings = bookings.filter(b => {
        const bookingScheduleDateId = b.scheduleDateID || b.scheduleDateId || b.scheduleDate?.scheduleDateID || b.scheduleDate?.scheduleDateId;
        return bookingScheduleDateId == routeInfo.scheduleDateId && 
               b.status !== 'Cancelled' && 
               b.paymentStatus !== 'Cancelled';
      });

      // Get booking seats
      const seatsData = [];
      for (const booking of activeBookings) {
        const bookingId = booking.bookingID || booking.bookingId || booking.id;
        if (bookingId) {
          try {
            const bookingSeatsResponse = await bookingApi.getBookingSeats(bookingId);
            let bookingSeats = [];
            if (bookingSeatsResponse?.value && Array.isArray(bookingSeatsResponse.value)) {
              bookingSeats = bookingSeatsResponse.value;
            } else if (Array.isArray(bookingSeatsResponse)) {
              bookingSeats = bookingSeatsResponse;
            }

            bookingSeats.forEach(bs => {
              seatsData.push({
                seatNumber: bs.seatNumber || bs.seat?.seatNumber || 'N/A',
                bookingID: bookingId,
                customerName: booking.customer?.customerName || booking.customerName || 'N/A',
                phone: booking.customer?.phone || booking.phone || 'N/A',
                price: booking.ticketPrice || booking.price || 0,
                paymentStatus: booking.paymentStatus || 'N/A'
              });
            });
          } catch (err) {
            console.error(`Error fetching seats for booking ${bookingId}:`, err);
          }
        }
      }

      setBookedSeatsData(seatsData);
    } catch (err) {
      console.error('Error loading schedule date and seats:', err);
      setError('Failed to load schedule information');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display as yyyy-MM-dd
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return 'N/A';
    }
  };

  // Format time for display (HH:mm)
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // Handle TimeSpan format (HH:mm:ss) or string format
      if (typeof timeString === 'string') {
        // If it's already in HH:mm format, return it
        if (timeString.match(/^\d{2}:\d{2}$/)) {
          return timeString;
        }
        // Try to parse as time
        const time = new Date(`2000-01-01T${timeString}`);
        if (!isNaN(time.getTime())) {
          const hours = String(time.getHours()).padStart(2, '0');
          const minutes = String(time.getMinutes()).padStart(2, '0');
          return `${hours}:${minutes}`;
        }
      }
      return timeString;
    } catch (error) {
      return 'N/A';
    }
  };

  const downloadSeatAvailabilityPDF = async () => {
    if (!selectedScheduleDateId || !selectedRouteId) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a route and date to download the report.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    // Allow download even if no bookings (will show empty report)
    if (bookedSeatsData.length === 0) {
      const result = await Swal.fire({
        icon: 'info',
        title: 'No Bookings Found',
        text: 'No bookings found for the selected route and date. Do you want to download an empty report?',
        showCancelButton: true,
        confirmText: 'Yes, Download',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3085d6'
      });
      
      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      console.log('Starting PDF generation...', {
        selectedScheduleDateId,
        selectedRouteId,
        bookedSeatsDataLength: bookedSeatsData?.length
      });

      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we prepare your report.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Fetch booking details for all booked seats
      const bookingsMap = new Map();
      const finalSeatsData = bookedSeatsData.length > 0 ? bookedSeatsData : [];
      
      for (const seatData of finalSeatsData) {
        if (seatData.bookingID && !bookingsMap.has(seatData.bookingID)) {
          try {
            const booking = await bookingApi.getBooking(seatData.bookingID);
            let bookingData = booking;
            if (booking?.value) bookingData = booking.value;
            else if (booking?.data) bookingData = booking.data;
            bookingsMap.set(seatData.bookingID, bookingData);
          } catch (error) {
            // Handle 404 or other errors gracefully - booking might be deleted
            if (error?.response?.status === 404 || error?.status === 404) {
              console.warn(`Booking ${seatData.bookingID} not found (404). Skipping...`);
            } else {
              console.error(`Error fetching booking ${seatData.bookingID}:`, error);
            }
            // Continue with other bookings even if one fails
          }
        }
      }

      // Create new PDF document in landscape (horizontal) format
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Get route and schedule info
      // Extract start and end cities
      let fromLocation = selectedRouteInfo?.routeName?.split(' -> ')[0] || 
                          scheduleDateDetails?.schedule?.route?.fromCityName ||
                          scheduleDateDetails?.schedule?.route?.fromLocation || 
                          scheduleDateDetails?.schedule?.route?.fromCity?.cityName || 'N/A';
      let toLocation = selectedRouteInfo?.routeName?.split(' -> ')[1] || 
                       scheduleDateDetails?.schedule?.route?.toCityName ||
                       scheduleDateDetails?.schedule?.route?.toLocation || 
                       scheduleDateDetails?.schedule?.route?.toCity?.cityName || 'N/A';
      
      // Trim whitespace from city names
      fromLocation = fromLocation.trim();
      toLocation = toLocation.trim();
      
      const routeName = `${fromLocation} → ${toLocation}`;
      const departureTime = formatTime(selectedRouteInfo?.departureTime || scheduleDateDetails?.departureTime || '');
      const arrivalTime = formatTime(selectedRouteInfo?.arrivalTime || scheduleDateDetails?.arrivalTime || '');
      const vehicle = selectedRouteInfo?.busNumber || 
                     scheduleDateDetails?.schedule?.vehicle?.registrationNumber || 
                     scheduleDateDetails?.schedule?.vehicle?.busNumber || 'N/A';
      const scheduleName = scheduleDateDetails?.schedule?.scheduleName || 
                          selectedRouteInfo?.scheduleName || 
                          'Standard Schedule';
      const bookingDate = selectedDate;
      const reportGeneratedAt = new Date().toLocaleString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });

      let yPos = 15;

      // Header with colored background box - height reduced to fit content exactly (no empty space)
      const headerStartY = yPos;
      const headerHeight = 38; // Reduced from 50 to 38 to remove empty space after departure/arrival
      doc.setFillColor(25, 58, 141); // Dark blue (blue-900)
      doc.roundedRect(10, headerStartY, 277, headerHeight, 2, 2, 'F'); // Draw dark blue background first
      
      yPos += 6;
      doc.setFontSize(18);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.text(COMPANY_CONFIG.appname, 148.5, yPos, { align: 'center' });
      
      yPos += 7;
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text('Seat Availability & Passenger Details Report', 148.5, yPos, { align: 'center' });
      
      yPos += 6;
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold'); // Set to bold for better visibility
      doc.setTextColor(255, 255, 255); // White text for blue background
      doc.text(`${fromLocation} - ${toLocation}`, 148.5, yPos, { align: 'center' });
      
      yPos += 5;
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255); // White text for blue background
      doc.text(`Vehicle No: ${vehicle} | Date: ${bookingDate}`, 148.5, yPos, { align: 'center' });
      
      yPos += 5;
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255); // White text for blue background
      doc.text(`Departure: ${departureTime || 'N/A'} | Arrival: ${arrivalTime || 'N/A'}`, 148.5, yPos, { align: 'center' });
      
      yPos += 5;
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(255, 255, 255); // White text for blue background
      doc.text(`Schedule: ${scheduleName}`, 148.5, yPos, { align: 'center' });

      // Move yPos to after the blue header box (white area)
      yPos = headerStartY + headerHeight + 3; // Start in white area with spacing

      // Passenger Details Section (no header, plain white area)
      yPos += 5; // Small spacing before table

      // Group bookings by bookingID and combine seat numbers
      const bookingGroups = new Map();
      
      // Handle case when there are no bookings
      if (finalSeatsData.length === 0) {
        console.log('No booked seats data available. Generating empty report.');
      }
      
      for (const seatData of finalSeatsData) {
        const bookingId = seatData.bookingID;
        if (!bookingId) continue;
        
        const booking = bookingsMap.get(bookingId);
        if (!booking) continue;
        
        if (!bookingGroups.has(bookingId)) {
          const customerName = booking.customer?.customerName || 
                              booking.Customer?.CustomerName ||
                              booking.customer?.CustomerName ||
                              booking.Customer?.customerName || 'N/A';
          const phone = booking.customer?.phone || 
                       booking.Customer?.Phone ||
                       booking.customer?.Phone ||
                       booking.Customer?.phone || 'N/A';
          const gender = booking.customer?.gender ||
                        booking.Customer?.Gender ||
                        booking.customer?.Gender ||
                        booking.Customer?.gender || '-';
          const paymentStatus = booking.paymentStatus || booking.PaymentStatus || 'Pending';
          const bookingDateStr = booking.bookingDate || booking.BookingDate;
          const formattedBookingDate = bookingDateStr 
            ? formatDate(bookingDateStr)
            : 'N/A';
          const totalAmount = booking.totalAmount || booking.TotalAmount || 0;
          const remark = booking.specialRequests || 
                        booking.SpecialRequests || 
                        booking.remark ||
                        booking.Remark || '-';
          
          // Get pickup and dropoff location names
          const pickupLocation = booking.pickupLocationName || 
                                booking.PickupLocationName ||
                                booking.pickupLocation?.cityName ||
                                booking.PickupLocation?.CityName || '-';
          const dropoffLocation = booking.dropoffLocationName || 
                                 booking.DropoffLocationName ||
                                 booking.dropoffLocation?.cityName ||
                                 booking.DropoffLocation?.CityName || '-';
          
          bookingGroups.set(bookingId, {
            bookingId: bookingId.toString(),
            customerName,
            phone,
            gender,
            paymentStatus,
            formattedBookingDate,
            totalAmount,
            remark: remark || '-',
            pickupLocation: pickupLocation || '-',
            dropoffLocation: dropoffLocation || '-',
            seatNumbers: []
          });
        }
        
        // Add seat number to the booking group
        const seatNumber = seatData.seatNumber || 'N/A';
        if (seatNumber !== 'N/A') {
          bookingGroups.get(bookingId).seatNumbers.push(seatNumber);
        }
      }

      // Check if any booking has a remark value (exclude empty, '-', 'N/A', null, undefined)
      let hasRemarks = false;
      for (const bookingData of bookingGroups.values()) {
        const remark = bookingData.remark;
        // Check if remark has actual content (not empty, not '-', not 'N/A', not null, not undefined)
        // Must be a non-empty string after trimming, and not equal to '-' or 'N/A'
        if (remark && 
            typeof remark === 'string') {
          const trimmedRemark = remark.trim();
          if (trimmedRemark !== '' && 
              trimmedRemark !== '-' && 
              trimmedRemark.toUpperCase() !== 'N/A') {
            hasRemarks = true;
            break;
          }
        }
      }
      
      // Prepare table data - one row per booking with comma-separated seat numbers
      const tableData = [];
      
      // If no bookings, add a message row
      if (bookingGroups.size === 0) {
        const row = [
          'N/A',
          'No bookings found',
          'N/A',
          'N/A',
          'N/A',
          'N/A',
          'N/A',
          'N/A',
          'N/A',
          '0.00'
        ];
        if (hasRemarks) {
          row.push('No bookings for this route and date');
        }
        tableData.push(row);
      } else {
        for (const bookingData of bookingGroups.values()) {
          const seatNumbersStr = bookingData.seatNumbers.sort((a, b) => {
            const numA = parseInt(a) || 0;
            const numB = parseInt(b) || 0;
            return numA - numB;
          }).join(', ');
          
          // Don't truncate passenger name - use full space (especially when Remarks column is hidden)
          const passengerName = bookingData.customerName || 'N/A';
          // Allow longer names when Remarks column is not shown
          const maxNameLength = hasRemarks ? 20 : 40;
          const truncatedPassengerName = passengerName.length > maxNameLength 
            ? passengerName.substring(0, maxNameLength) 
            : passengerName;
          
          const row = [
            bookingData.bookingId,
            truncatedPassengerName,
            bookingData.phone,
            bookingData.gender,
            seatNumbersStr || 'N/A',
            bookingData.pickupLocation || '-',
            bookingData.dropoffLocation || '-',
            bookingData.paymentStatus,
            bookingData.formattedBookingDate,
            `${bookingData.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          ];
          
          // Only add remark if there are remarks in the data
          if (hasRemarks) {
            // Show empty string or '-' for empty remarks (don't show '-')
            const remarkValue = bookingData.remark;
            const displayRemark = (remarkValue && remarkValue !== '' && remarkValue !== '-' && remarkValue !== 'N/A' && remarkValue.trim() !== '') 
              ? remarkValue 
              : ''; // Show empty string instead of '-' for empty remarks
            row.push(displayRemark);
          }
          
          tableData.push(row);
        }
      }

      // Comprehensive table with all passenger details
      // Check if autoTable is available, if not use manual table
      let finalY = yPos;
      
      if (doc.autoTable) {
        try {
      // Create header array conditionally based on whether remarks exist
      const headers = ['Booking ID', 'Passenger', 'Phone', 'Gender', 'Seat Numbers', 'Pickup Location', 'Dropoff Location', 'Payment Status', 'Booked', 'Amount'];
      if (hasRemarks) {
        headers.push('Remarks');
      }
      
      // Professional table design: Compact column widths to fit all details
      // A4 Landscape width = 297mm, usable width ~277mm (minus margins)
      console.log('📊 Generating PDF with improved table design...');
      const basePadding = { top: 5, bottom: 5, left: 4, right: 4 }; // Reduced padding for compact fit
      const columnStyles = {
        0: { cellWidth: 20, halign: 'left', cellPadding: basePadding }, // Booking ID
        1: { cellWidth: hasRemarks ? 38 : 50, overflow: 'linebreak', halign: 'left', cellPadding: basePadding }, // Passenger Name - reduced
        2: { cellWidth: 25, cellPadding: basePadding }, // Phone
        3: { cellWidth: 15, cellPadding: basePadding }, // Gender
        4: { cellWidth: 28, cellPadding: basePadding }, // Seat Numbers
        5: { cellWidth: 30, cellPadding: basePadding }, // Pickup Location
        6: { cellWidth: 30, cellPadding: basePadding }, // Dropoff Location
        7: { cellWidth: 25, cellPadding: basePadding }, // Payment Status
        8: { cellWidth: 22, cellPadding: basePadding }, // Booked (date only - 10 chars)
        9: { cellWidth: 22, halign: 'right', cellPadding: basePadding } // Amount - right aligned for numbers
      };
      
      // Only add Remarks column style if there are remarks
      if (hasRemarks) {
        columnStyles[10] = { cellWidth: 30, cellPadding: basePadding }; // Remarks - reduced
      }
      
      doc.autoTable({
        startY: yPos,
            head: [headers],
        body: tableData,
        headStyles: {
          fillColor: [30, 64, 175], // Darker blue for better contrast
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10, // Slightly larger for visibility
          lineWidth: 0.5,
          cellPadding: basePadding,
          halign: 'right' // Right align headers (except first column)
        },
        bodyStyles: {
          fontSize: 8.5,
          lineWidth: 0.5,
          lineColor: [200, 200, 200], // Light gray lines
          cellPadding: basePadding,
          textColor: [51, 51, 51], // Dark gray text for better readability
          halign: 'left',
          fontStyle: 'normal'
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251], // Very light gray for subtle row separation
          cellPadding: basePadding,
          halign: 'left'
        },
        columnStyles: columnStyles,
            margin: { top: yPos, left: 10, right: 10 },
            // Use full width in landscape mode (A4 landscape = 297mm width)
            tableWidth: 'auto',
        styles: {
          lineColor: [200, 200, 200],
          lineWidth: 0.5,
          overflow: 'linebreak',
          cellPadding: basePadding,
          halign: 'left'
        },
            didDrawPage: function (data) {
              // Add footer with page number and generation date
              doc.setFontSize(7);
              doc.setTextColor(128, 128, 128);
              const pageText = `Page ${data.pageNumber} of ${data.pageCount || 1}`;
              doc.text(
                pageText,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 8,
                { align: 'center' }
              );
              doc.text(
                `Generated on: ${reportGeneratedAt}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 4,
                { align: 'center' }
              );
            }
          });
          
          // Get final Y position from autoTable if available
          if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
            finalY = doc.lastAutoTable.finalY;
          } else {
            // Estimate final Y position
            finalY = yPos + (tableData.length * 8) + 20;
          }
        } catch (error) {
          console.error('Error using autoTable:', error);
          console.error('Error details:', error.message, error.stack);
          // Fall through to manual table
          finalY = yPos;
        }
      }
      
      // If autoTable failed or is not available, use manual table
      if (finalY === yPos || !doc.autoTable) {
        // Fallback: Create simple table manually with improved design
        console.warn('Using manual table generation');
        let tableY = yPos;
        const rowHeight = 6; // Compact row height
        // Compact column widths to fit all details on page (A4 landscape = 297mm, usable ~277mm)
        const colWidths = [20, hasRemarks ? 38 : 50, 25, 15, 28, 30, 30, 25, 22, 22]; // Base columns - compact (Booked date reduced from 28 to 22)
        if (hasRemarks) {
          colWidths.push(30); // Add Remarks column width - compact
        }
        let xPos = 10; // Start from margin
        
        // Draw header with improved styling
        doc.setFont(undefined, 'bold');
        doc.setFontSize(10); // Larger header font
        doc.setTextColor(255, 255, 255); // White text
        doc.setFillColor(30, 64, 175); // Darker blue background
        // Draw header background rectangle
        doc.roundedRect(10, tableY - 5, 277, rowHeight + 2, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        
        const headers = ['ID', 'Passenger', 'Phone', 'Gender', 'Seat Numbers', 'Pickup', 'Dropoff', 'Payment ', 'Booked', 'Amount'];
        if (hasRemarks) {
          headers.push('Remarks');
        }
        headers.forEach((header, i) => {
          // Right align headers except first
          const align = i === 0 ? 'left' : 'right';
          doc.text(header, xPos + (i === 0 ? 3 : colWidths[i] - 3), tableY, { align: align });
          xPos += colWidths[i];
        });
        tableY += rowHeight + 3;
        
        // Reset text color and draw header line
        doc.setTextColor(0, 0, 0);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(10, tableY, 287, tableY);
        tableY += 5; // Gap after header line
        
        // Draw rows with improved styling
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8.5); // Slightly larger body font
        doc.setTextColor(51, 51, 51); // Dark gray text
        tableData.forEach((row, rowIndex) => {
          if (tableY > doc.internal.pageSize.height - 20) {
            doc.addPage();
            tableY = 20;
          }
          
          // Alternate row background color
          if (rowIndex % 2 === 1) {
            doc.setFillColor(249, 250, 251);
            doc.roundedRect(10, tableY - 4, 277, rowHeight + 1, 1, 1, 'F');
          }
          
          xPos = 10;
          row.forEach((cell, colIndex) => {
            const cellText = String(cell || '').trim();
            // Adjust maxLength for compact columns - allow more characters to fit
            const maxLength = colIndex === 1 ? 30 : (colIndex === 4 ? 35 : (colIndex === 5 || colIndex === 6 ? 32 : (colIndex === 10 ? 30 : 25)));
            const displayText = cellText.length > maxLength ? cellText.substring(0, maxLength - 3) + '...' : cellText;
            
            // Right align for amount column (last data column), left for others
            const align = (colIndex === 9) ? 'right' : 'left';
            doc.text(displayText, xPos + (align === 'right' ? colWidths[colIndex] - 3 : 3), tableY, { align: align });
            xPos += colWidths[colIndex];
          });
          tableY += rowHeight + 2; // Slightly more spacing between rows
        });
        
        // Update finalY for manual table
        finalY = tableY + 5;
      }

      // Calculate summary statistics
      const totalBookings = bookingGroups.size;
      const totalBookedSeats = finalSeatsData.length;
      
      // Count completed and pending payments
      let completedPaymentCount = 0;
      let pendingPaymentCount = 0;
      
      for (const bookingData of bookingGroups.values()) {
        const status = String(bookingData.paymentStatus).toLowerCase();
        if (status === 'completed' || status === '1' || bookingData.paymentStatus === 1 || bookingData.paymentStatus === 'Completed') {
          completedPaymentCount++;
        } else {
          pendingPaymentCount++;
        }
      }
      
      // Add Summary Statistics Section
      yPos = finalY + 10;
      if (yPos > doc.internal.pageSize.height - 40) {
        doc.addPage();
        yPos = 20;
      }
      
      // Summary Statistics Box
      doc.setFillColor(239, 246, 255); // Blue-50
      doc.roundedRect(10, yPos, 277, 25, 2, 2, 'F');
      
      yPos += 5;
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Summary Statistics', 15, yPos);
      
      yPos += 7;
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      
      const summaryLeftX = 15;
      const summaryRightX = 150;
      let summaryY = yPos;
      
      doc.setFont(undefined, 'bold');
      doc.text('Total Bookings:', summaryLeftX, summaryY);
      doc.setFont(undefined, 'normal');
      doc.text(totalBookings.toString(), summaryLeftX + 35, summaryY);
      
      doc.setFont(undefined, 'bold');
      doc.text('Total Booked Seats:', summaryRightX, summaryY);
      doc.setFont(undefined, 'normal');
      doc.text(totalBookedSeats.toString(), summaryRightX + 40, summaryY);
      
      summaryY += 5;
      doc.setFont(undefined, 'bold');
      doc.text('Pending Payment Count:', summaryLeftX, summaryY);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(234, 179, 8); // Yellow
      doc.text(pendingPaymentCount.toString(), summaryLeftX + 50, summaryY);
      doc.setTextColor(0, 0, 0);
      
      doc.setFont(undefined, 'bold');
      doc.text('Completed Payment Count:', summaryRightX, summaryY);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(34, 197, 94); // Green
      doc.text(completedPaymentCount.toString(), summaryRightX + 55, summaryY);
      doc.setTextColor(0, 0, 0);
      
      // Add footer to last page if not already added by autoTable
      yPos = summaryY + 15;
      if (yPos > doc.internal.pageSize.height - 20) {
        yPos = doc.internal.pageSize.height - 15;
      }
      doc.setFontSize(7);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Generated on: ${reportGeneratedAt}`,
        doc.internal.pageSize.width / 2,
        yPos,
        { align: 'center' }
      );

      // Save PDF
      const fileName = `Route_Booking_Details_${selectedDate}_${selectedRouteId}.pdf`;
      
      console.log('Attempting to save PDF:', fileName);
      
      try {
        // Save the PDF using jsPDF's save method
      doc.save(fileName);
        console.log('PDF saved successfully using doc.save()');
        
        Swal.close();
        Swal.fire({
          icon: 'success',
          title: 'Download Complete',
          text: 'PDF report with route, schedule, and passenger details downloaded successfully!',
          confirmButtonColor: '#2563eb'
        });
      } catch (saveError) {
        console.error('Error saving PDF with doc.save():', saveError);
        // Try alternative method - create blob and download
        try {
          console.log('Trying alternative blob download method...');
          const pdfBlob = doc.output('blob');
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          
          // Clean up after a short delay
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }, 100);
          
          console.log('PDF downloaded successfully using blob method');

      Swal.close();
      Swal.fire({
        icon: 'success',
        title: 'Download Complete',
        text: 'PDF report downloaded successfully!',
        confirmButtonColor: '#2563eb'
      });
        } catch (blobError) {
          console.error('Error with blob download:', blobError);
          throw blobError;
        }
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        selectedScheduleDateId,
        selectedRouteId,
        bookedSeatsDataLength: bookedSeatsData?.length
      });
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error Generating PDF',
        html: `Failed to generate PDF. Please check the console for details.<br/><br/>Error: ${error.message || 'Unknown error'}`,
        confirmButtonColor: '#dc2626'
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Redirect to login if not authenticated
  if (!user || user.role !== 3) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Seat Status</h1>
              <p className="text-sm text-gray-600">Welcome, Bus {user.username}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Selection Form - Vertical Layout */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Seat Availability</h3>
          
          {/* Date, Route Selection, and Download Button - Vertical Layout */}
          <div className="space-y-4">
            {/* Date - Today's date (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                disabled
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
              />
            </div>

            {/* Route Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Select Route
              </label>
              <select
                value={selectedRouteId}
                onChange={async (e) => {
                  const selected = availableRoutes.find(r => 
                    `${r.routeId}_${r.scheduleId}` === e.target.value
                  );
                  if (selected) {
                    setSelectedRouteId(e.target.value);
                    setSelectedScheduleDateId(selected.scheduleDateId);
                    setSelectedRouteInfo(selected);
                    
                    // Load schedule date details and seats
                    try {
                      setLoading(true);
                      const scheduleDateResponse = await scheduleApi.getScheduleDate(selected.scheduleDateId);
                      let scheduleDateData = scheduleDateResponse?.value || scheduleDateResponse?.data || scheduleDateResponse;
                      setScheduleDateDetails(scheduleDateData);

                      // Get bookings for this schedule date
                      const bookingsResponse = await bookingApi.getAllBookings({
                        fromDate: selectedDate,
                        toDate: selectedDate
                      });

                      let bookings = [];
                      if (bookingsResponse?.value && Array.isArray(bookingsResponse.value)) {
                        bookings = bookingsResponse.value;
                      } else if (Array.isArray(bookingsResponse)) {
                        bookings = bookingsResponse;
                      }

                      // Filter bookings by schedule date ID and non-cancelled status
                      const activeBookings = bookings.filter(b => {
                        const bookingScheduleDateId = b.scheduleDateID || b.scheduleDateId || b.scheduleDate?.scheduleDateID || b.scheduleDate?.scheduleDateId;
                        return bookingScheduleDateId == selected.scheduleDateId && 
                               b.status !== 'Cancelled' && 
                               b.paymentStatus !== 'Cancelled';
                      });

                      // Get booking seats
                      const seatsData = [];
                      for (const booking of activeBookings) {
                        const bookingId = booking.bookingID || booking.bookingId || booking.id;
                        if (bookingId) {
                          try {
                            const bookingSeatsResponse = await bookingApi.getBookingSeats(bookingId);
                            let bookingSeats = [];
                            if (bookingSeatsResponse?.value && Array.isArray(bookingSeatsResponse.value)) {
                              bookingSeats = bookingSeatsResponse.value;
                            } else if (Array.isArray(bookingSeatsResponse)) {
                              bookingSeats = bookingSeatsResponse;
                            }

                            bookingSeats.forEach(bs => {
                              seatsData.push({
                                seatNumber: bs.seatNumber || bs.seat?.seatNumber || 'N/A',
                                bookingID: bookingId,
                                customerName: booking.customer?.customerName || booking.customerName || 'N/A',
                                phone: booking.customer?.phone || booking.phone || 'N/A',
                                price: booking.ticketPrice || booking.price || 0,
                                paymentStatus: booking.paymentStatus || 'N/A'
                              });
                            });
                          } catch (err) {
                            console.error(`Error fetching seats for booking ${bookingId}:`, err);
                          }
                        }
                      }

                      setBookedSeatsData(seatsData);
                    } catch (err) {
                      console.error('Error loading schedule date and seats:', err);
                      setError('Failed to load schedule information');
                    } finally {
                      setLoading(false);
                    }
                  } else {
                    // Clear selection if route not found
                    setSelectedRouteId('');
                    setSelectedScheduleDateId('');
                    setSelectedRouteInfo(null);
                    setScheduleDateDetails(null);
                    setBookedSeatsData([]);
                  }
                }}
                disabled={loadingRoutes || availableRoutes.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">
                  {loadingRoutes ? 'Loading routes...' : availableRoutes.length === 0 ? 'No routes available for today' : 'Select a route'}
                </option>
                {availableRoutes.map((route, index) => (
                  <option key={index} value={`${route.routeId}_${route.scheduleId}`}>
                    {route.routeName} - {route.scheduleName} ({route.departureTime})
                  </option>
                ))}
              </select>
            </div>

            {/* Download Button */}
            <div>
              <button
                onClick={downloadSeatAvailabilityPDF}
                disabled={!selectedScheduleDateId || !selectedRouteId || loading}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                title={!selectedScheduleDateId || !selectedRouteId ? 'Please select a route to download PDF' : 'Download Seat Availability Report'}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Download Seat Availability PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Empty State */}
        {!selectedRouteId && availableRoutes.length === 0 && !loadingRoutes && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No routes available for today</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeatStatus;
